import * as angular from "angular";
import {ItinerariesTasksSettings} from "../../agents/angular/models/itineraries-tasks-settings.model";
import {PlanningRouteOverviewSettingsModel} from "../models/planning-route-overview-settings.model";
import {PlanningViewSettingsModel} from "../models/planning-view-settings.model";
import {SelectorType} from "../enums/selector-type.enum";
import {
  draftJobChangeTypeColorMap,
  draftJobChangeStatusColorMap,
  draftJobChangesSyncStateColorMap
} from "./draft-job-changes-color-maps";

const defaultCoreOptimizationSetup = {
  radialBest: 1.0,
  radialRegret: 0.0,
  minRadialShare: 0,
  maxRadialShare: 0,
  randomBest: 0.0,
  randomRegret: 0.0,
  minRandomShare: 0,
  maxRandomShare: 0,
  worstBest: 0.0,
  worstRegret: 0.0,
  minWorstShare: 0,
  maxWorstShare: 0,
  clusterBest: 0.0,
  clusterRegret: 0.0,
  minClusterShare: 0,
  maxClusterShare: 0,
  stringBest: 0.0,
  stringRegret: 0.0,
  customClusterWeight: 0.0,
  customRadialWeight: 1.0,
  customRandomWeight: 0.0,
  customWorstWeight: 0.0,
  customStringWeight: 0.0,
  stringKmin: 0,
  stringKmax: 0,
  stringLmin: 0,
  stringLmax: 0,
  threshHoldAlpha: 0.0,
  insertionNoiseLevel: 0.0,
  insertionNoisePrb: 0.0,
  considerFixedCost: false,
  fixedCost: 0.0,
  allowSwitch: false,
  selectorAlpha: 0.0,
  selectorFloor: 0.0,
  solutionMemory: 0,
  selectorType: SelectorType.Best
};

const projectStatusColorMap = {
  SCHEME: '#F0EAF6',
  RUNNING: '#F0EAF6',
  IMPORT: '#F0EAF6',
  PARTIAL_DISPATCHED: '#DEF2FC',
  DISPATCHED: '#E9F7E9'
};


const routeColors = [
  '#fac70b',
  '#FD72AD',
  '#A561FF',
  '#1F9EC7',
  '#00D8B1',
  '#D4FFA3',
  '#ADD1FC',
  '#0C4B8E',
  '#189BFA',
  '#feed30',
  '#B3E55E',
  '#51AF5B',
  '#36162E',
  '#594057',
  '#B14B4B',
  '#FF6D3F',
  '#65C0BA',
  '#216583',
  '#F76262',
  '#D6C5F0',
  '#5727A3',
  '#FE4E6E',
  '#EDF68D',
  '#377FD9',
  '#83CBFB',
  '#765A60',
  '#7D0000',
  '#C95501',
  '#EAA81B',
  '#AA5C9F',
  '#FFCECE',
  '#F78536',
  '#6A7EFC',
  '#D32D2D',
  '#EE7CFF'
];

const trafficStates = [
  'NO_TRAFFIC',
  'SOME',
  'HIGH',
  'VERY_HIGH',
  'GRIDLOCK'
];

const optimizationTypes = [
  'DISTANCE',
  'TIME',
  'COMBINE'
];

const jobsSourceTypes = [
  'DISPATCH',
  'OMS',
  'MANUAL'
];

const resourcesSourceTypes = [
  'Allocation',
  'Vehicle'
];

const roadRestrictions = [
  'MINIMIZE',
  'AVOID',
  'NONE'
];

const locationTypes = [
  'DEPOT',
  'CUSTOM',
  // 'RESOURCE'
];

const resourceActions = [
  {key: 'ALLOCATIONS', name: 'Add allocations', icon: 'rotate'},
  {key: 'VEHICLES', name: 'Add vehicle types', icon: 'car'}
];

const activityActions = [
  {key: 'INFO', name: 'Stop Info', icon: 'info'},
  {key: 'LINKED', name: 'Inspect Linked Services', icon: 'linked'},
  {key: 'REMOVE', name: 'Move To Leftovers', icon: 'minus-circle'}
];

const defaultViewSettings: PlanningViewSettingsModel = {
  jobs: {
    columns: {
      id: true,
      skills: true,
      account: true,
      vehicles: false,
      contacts: true,
      timeWindow: true,
      address: true,
      serviceTime: false,
      sla: true,
      packages: true,
      labels: false,
      tags: false,
      accountExternalId: false,
      leftoverReason: false,
      recurringServiceTemplateId: false
    },
    load: {
      weight: false,
      volume: false,
      packages: false,
      value: false,
      spots: false,
      pallets: false
    },
    general: {
      autoMoveSelectionToTop: true
    }
  },
  routes: {
    columns: {
      id: false,
      status: true,
      skills: true,
      labels: true,
      vehicle: true,
      agent: false,
      income: false,
      value: false,
      timeWindow: true,
      duration: false,
      distance: false,
      jobsCount: true,
      activitiesCount: false,
      accounts: false,
      accountExternalId: false,
      pickingHour: false,
      carrier: true
    },
    load: {
      weight: false,
      volume: false,
      packages: false,
      value: false,
      spots: false,
      pallets: false
    },
    capacity: {
      weight: false,
      volume: false,
      pallets: false
    }
  },
  route: {
    columns: {
      id: true,
      type: true,
      account: true,
      accountExternalId: false,
      contacts: true,
      distance: false,
      timeWindow: true,
      timeEstimations: true,
      address: true,
      serviceTime: true,
      sla: false,
      packages: true,
      barcodes: false,
      labels: false,
      vehicles: false,
      recurringServiceTemplateId: false
    },
    load: {
      weight: false,
      volume: false,
      packages: false,
      value: false,
      spots: false,
      pallets: false
    },
    capacity: {
      weight: false,
      volume: false,
      pallets: false
    }
  },
  resources: {
    columns: {
      skills: true,
      labels: true,
      vehicle: true,
      logisticVehicle: true,
      agent: true,
      timeWindow: true,
      carrier: true,
      routesCount: true,
      routesNames: true,
      totalDuration: true,
      tags: false,
    },
    load: {
      weight: false,
      volume: false,
      packages: false,
      value: false,
      spots: false,
      pallets: false
    },
    capacity: {
      weight: false,
      volume: false,
      pallets: false
    }
  },
  draft: {
    columns: {
      createdAt: true,
      targetDate: true,
      routes: true,
      accounts: true,
      services: true,
      routedServices: true,
      stops: true,
      conflicts: true,
      routedStops: true,
      leftovers: true,
      totalResources: true,
      resourceInUse: true,
      trips: true,
      packages: true,
      weight: true,
      volume: true,
      spots: true,
      pallets: true,
      mileage: true,
      workingTime: true,
      routesTime: true,
      drivingTime: true,
      idleTime: true,
      price: true,
      income: true,
      value: true,
      weightUtilization: true,
      volumeUtilization: true,
      packagesUtilization: true,
      spotsUtilization: true,
      palletsUtilization: true
    }
  }
}

const states = {
  templatesOld: 'planning.projects-old.templates',
  projectOld: 'planning.project-old.scheme.project',
  draftOld: 'planning.project-old.scheme.draft',
  completedOld: 'planning.project-old.completed',
  routesOld: 'planning.project-old.routes',

  projectsCalendar: 'planning.projects.scheduled.calendar',
  templates: 'planning.projects.templates',
  project: 'planning.project.project',
  draft: 'planning.project.draft',
  completed: 'planning.project.completed',
  routes: 'planning.project.routes'
}

const loadingMessages = [
  'Gathering data, please wait',
  'Clever algorithm is now involved',
  'Magic is happening, we\'re almost there',
  'Pretty much done, just fine tuning'
];

const projectRouteOverviewTableSections: PlanningRouteOverviewSettingsModel = {
  sections: {
    account: true,
    capacity: true,
    timeWindow: true,
    packageType: true,
    packageBarcode: true,
    price: true,
    labels: true
    // income: true,
  }
}

angular
  .module('bringoz.admin.planning')
  .constant('planningConstants', {
    projectStatusColorMap: projectStatusColorMap,
    draftJobChangeTypeColorMap: draftJobChangeTypeColorMap,
    draftJobChangeStatusColorMap: draftJobChangeStatusColorMap,
    draftJobChangesSyncStateColorMap: draftJobChangesSyncStateColorMap,
    routeColors: routeColors,
    trafficStates: trafficStates,
    optimizationTypes: optimizationTypes,
    resourcesSourceTypes: resourcesSourceTypes,
    jobsSourceTypes: jobsSourceTypes,
    roadRestrictions: roadRestrictions,
    locationTypes: locationTypes,
    resourceActions: resourceActions,
    activityActions: activityActions,
    defaultViewSettings: defaultViewSettings,
    states: states,
    loadingMessages: loadingMessages,
    projectRouteOverviewTableSections: projectRouteOverviewTableSections,
    defaultCoreOptimizationSetup: defaultCoreOptimizationSetup
  });
